// frontend/src/hooks/chat/useChatEvents.ts
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useStore } from '../../store';
import {
  selectChatSurfaceOpen,
  selectVisibleChannelIds,
} from '../../store/slices/chatSlice';
import { events, type EventStreamState } from '../../services/events';
import {
  appendMessagesAfter,
  chatKeys,
  flattenMessages,
  hasFetchedHistory,
  lastFetchedSeq,
  MESSAGE_PAGE_SIZE,
  refetchInboxCoalesced,
  type MessagePages,
} from './useChatQueries';
import { useMarkRead } from './useChatMutations';
import { invalidateAttachmentUrl } from './useAttachmentUrls';

/**
 * Turns user-event stream frames into cache work. A frame carries ids only, so nothing
 * here renders a frame: the body always arrives through the normal API.
 * Mounted once, by ChatTray.
 */
export function useChatEvents(): void {
  const queryClient = useQueryClient();
  const markRead = useMarkRead();
  // Read from the store when a frame lands, not through render state: the
  // subscription is registered once, and the answer must describe the screen at
  // that moment. Minimized windows and a closed dock or sheet show nothing, so
  // a channel the person cannot see is never marked read.
  const isVisible = (channelId: string): boolean =>
    selectVisibleChannelIds(useStore.getState()).includes(channelId);

  useEffect(() => {
    // The cursor skips rows that only a send response cached, so a message
    // another person sent just before that send is still fetched.
    const lastSeq = (channelId: string): number =>
      lastFetchedSeq(
        queryClient,
        channelId,
        flattenMessages(
          (
            queryClient.getQueryData(
              chatKeys.messages(channelId)
            ) as MessagePages
          )?.pages
        )
      );

    // One catch-up per channel. A frame that lands while it runs sets `again`,
    // so the loop fetches once more instead of starting a parallel fetch with
    // the same cursor, and a burst larger than a page drains page by page.
    const catchUps = new Map<string, { again: boolean }>();
    // Channels whose frame landed before their first history page loaded. That
    // page may have been built before the row committed, so the catch-up runs
    // once the page is cached.
    const waitingForHistory = new Set<string>();
    const catchUp = async (channelId: string): Promise<void> => {
      const running = catchUps.get(channelId);
      if (running) {
        running.again = true;
        return;
      }
      const state = { again: false };
      catchUps.set(channelId, state);
      try {
        for (;;) {
          state.again = false;
          // Until the first history page loads, `after=0` would drain the
          // channel from its oldest row. That load brings the newest page, and
          // the open conversation marks read from it. After it, `after=0` means the
          // channel had no fetched row, so it brings exactly the first rows.
          if (!hasFetchedHistory(queryClient, channelId)) {
            waitingForHistory.add(channelId);
            return;
          }
          const after = lastSeq(channelId);
          const count = await appendMessagesAfter(
            queryClient,
            channelId,
            after
          );
          if (count < MESSAGE_PAGE_SIZE && !state.again) break;
        }
      } finally {
        catchUps.delete(channelId);
      }
      // Re-checked after the last page: the person may have closed the panel,
      // left the channel or hidden the tab while the fetch ran. An empty
      // channel whose fetch brought nothing has no seq to mark read.
      const seq = lastSeq(channelId);
      if (
        seq > 0 &&
        isVisible(channelId) &&
        document.visibilityState === 'visible'
      ) {
        markRead(channelId, seq);
        return;
      }
      // The read was withheld, so the row stays unread and mark-read's own
      // refetch never runs. Refresh the inbox here instead, or the launcher
      // badge, the tab title and the inbox row keep showing no unread for a
      // message the person never saw.
      refetchInboxCoalesced(queryClient);
    };

    // Query 5 joins a pending fetch for a query that has no data instead of
    // cancelling it, so a response the server built before the change
    // committed would stay on screen. Wait for that first fetch to settle,
    // then invalidate, which refetches now that none is running. Layer 4
    // reuses this for chat.message_updated.
    const refetchHistory = async (channelId: string): Promise<void> => {
      const query = queryClient.getQueryCache().find({
        queryKey: chatKeys.messages(channelId),
        exact: true,
      });
      if (
        query &&
        query.state.fetchStatus !== 'idle' &&
        query.state.data === undefined
      ) {
        // The invalidation below retries the load, so a failed first fetch
        // here needs no error handling of its own.
        await query.promise?.catch(() => {});
      }
      await queryClient.invalidateQueries({
        queryKey: chatKeys.messages(channelId),
      });
    };

    // The inbox mirror of the wait in `refetchHistory`. Query 5 returns the
    // running promise for a query that is fetching with no data yet, so a
    // refresh asked for while the inbox's first load is in flight is swallowed
    // and the response (built before the edit or delete) lands as the final
    // preview, unread count and `@` badge. Wait for that load, then ask again:
    // the coalescing throttle makes the second ask a fresh request, now or on
    // its trailing timer.
    const refetchInboxAfterFirstLoad = async (): Promise<void> => {
      const query = queryClient.getQueryCache().find({
        queryKey: chatKeys.inbox(),
        exact: true,
      });
      if (
        !query ||
        query.state.fetchStatus === 'idle' ||
        query.state.data !== undefined
      ) {
        return;
      }
      // A failed first load still needs the follow-up ask, so ignore its error
      // here; the refetch below reports through the query's own state.
      await query.promise?.catch(() => {});
      refetchInboxCoalesced(queryClient);
    };

    const unsubscribeHistory = queryClient.getQueryCache().subscribe(event => {
      if (waitingForHistory.size === 0) return;
      if (event.type !== 'updated' || event.action.type !== 'success') return;
      for (const channelId of waitingForHistory) {
        if (!hasFetchedHistory(queryClient, channelId)) continue;
        waitingForHistory.delete(channelId);
        // The catch-up re-checks the panel and the tab before it marks read.
        void catchUp(channelId).catch(error =>
          console.error('Chat live fetch failed', error)
        );
      }
    });

    const unsubscribeMessage = events.subscribe('chat.message', frame => {
      const channelId = frame.payload.channel_id;
      const isOpen = isVisible(channelId);

      if (isOpen && document.visibilityState === 'visible') {
        // No inbox refetch here: one that starts before the mark-read POST
        // commits brings back unread_count 1. Mark-read refetches the inbox
        // after it succeeds, which also brings the new preview for Back.
        void catchUp(channelId).catch(error =>
          console.error('Chat live fetch failed', error)
        );
        return;
      }
      refetchInboxCoalesced(queryClient);
    });

    // A preview is ready: drop the cached URL pair (it had no preview_url) and
    // refetch the history, so the message carries status 'ready'.
    const unsubscribeAttachment = events.subscribe('chat.attachment', frame => {
      invalidateAttachmentUrl(frame.payload.attachment_id);
      void refetchHistory(frame.payload.channel_id);
    });

    // Someone reacted: refetch the history. The frame carries IDs only.
    const unsubscribeReaction = events.subscribe('chat.reaction', frame => {
      void refetchHistory(frame.payload.channel_id);
    });

    // An edit or a delete can change the newest preview, the unread count and
    // the `@` badge, and the server owns those values. So refetch the history
    // and the inbox. The frame carries IDs only, like the others here.
    const unsubscribeUpdated = events.subscribe(
      'chat.message_updated',
      frame => {
        void refetchHistory(frame.payload.channel_id);
        refetchInboxCoalesced(queryClient);
        void refetchInboxAfterFirstLoad();
      }
    );

    const unsubscribeReset = events.onReset(() => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.inbox() });
      for (const channelId of selectVisibleChannelIds(useStore.getState())) {
        void queryClient.invalidateQueries({
          queryKey: chatKeys.messages(channelId),
        });
      }
    });

    // Same pattern as the /auth/me visibility refetch in App.tsx.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void queryClient.invalidateQueries({ queryKey: chatKeys.inbox() });
      for (const channelId of selectVisibleChannelIds(useStore.getState())) {
        void queryClient.invalidateQueries({
          queryKey: chatKeys.messages(channelId),
        });
        // A read the catch-up withheld while the tab was hidden is still owed:
        // those rows are already cached, so nothing else would ever send it.
        // `markRead` re-checks the tab and the visible windows for itself.
        const seq = lastSeq(channelId);
        if (seq > 0) {
          markRead(channelId, seq);
        }
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      unsubscribeHistory();
      unsubscribeMessage();
      unsubscribeAttachment();
      unsubscribeReaction();
      unsubscribeUpdated();
      unsubscribeReset();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [queryClient, markRead]);
}

/**
 * The wrapper publishes `state` as a property, not a subscription, so the strip
 * samples it while a chat surface is open.
 */
export function useEventsConnectionState(): EventStreamState {
  const surfaceOpen = useStore(selectChatSurfaceOpen);
  const [state, setState] = useState<EventStreamState>(events.state);
  useEffect(() => {
    if (!surfaceOpen) return;
    setState(events.state);
    const id = setInterval(() => setState(events.state), 1000);
    return () => clearInterval(id);
  }, [surfaceOpen]);
  return state;
}
